import {
	Editor,
	isEqualAllowingForFloatingPointErrors,
	TLArrowShape,
	TLShapeId,
} from '@tldraw/editor'
import { createComputedCache } from '@tldraw/store'
import { STROKE_SIZES } from '../shared/default-shape-constants'
import { getDisplayValues } from '../shared/getDisplayValues'
import { TLArrowInfo } from './arrow-types'
import { getCurvedArrowInfo } from './curved-arrow'
import { getElbowArrowInfo } from './elbow/getElbowArrowInfo'
import { getArrowBindings, getIsArrowStraight } from './shared'
import { getStraightArrowInfo } from './straight-arrow'

const arrowInfoCache = createComputedCache<Editor, TLArrowInfo, TLArrowShape>(
	'arrow info',
	(editor: Editor, shape: TLArrowShape): TLArrowInfo => {
		const bindings = getArrowBindings(editor, shape)
		const util = editor.getShapeUtil(shape)
		// A replacement arrow util may not carry display values; fall back to the theme width.
		const sw =
			'getDefaultDisplayValues' in util.options && 'getCustomDisplayValues' in util.options
				? (getDisplayValues(util as any, shape) as { strokeWidth: number }).strokeWidth
				: editor.getCurrentTheme().strokeWidth * STROKE_SIZES[shape.props.size]

		if (shape.props.kind === 'elbow') {
			const elbowInfo = getElbowArrowInfo(editor, shape, bindings, sw)
			if (!elbowInfo?.route) return getStraightArrowInfo(editor, shape, bindings, sw)

			const start = elbowInfo.swapOrder ? elbowInfo.B : elbowInfo.A
			const end = elbowInfo.swapOrder ? elbowInfo.A : elbowInfo.B

			return {
				type: 'elbow',
				bindings,
				start: {
					handle: start.target,
					point: elbowInfo.route.points[0],
					arrowhead: shape.props.arrowheadStart,
				},
				end: {
					handle: end.target,
					point: elbowInfo.route.points[elbowInfo.route.points.length - 1],
					arrowhead: shape.props.arrowheadEnd,
				},
				elbow: elbowInfo,
				route: elbowInfo.route,
				isValid: true,
			}
		}

		if (getIsArrowStraight(shape)) {
			return getStraightArrowInfo(editor, shape, bindings, sw)
		} else {
			return getCurvedArrowInfo(editor, shape, bindings, sw)
		}
	},
	{
		areRecordsEqual: (a, b) => a.props === b.props,
		areResultsEqual: isEqualAllowingForFloatingPointErrors,
	}
)

/** @public */
export function getArrowInfo(editor: Editor, shape: TLArrowShape | TLShapeId) {
	const id = typeof shape === 'string' ? shape : shape.id
	return arrowInfoCache.get(editor, id)
}
